import React, { useEffect, useState } from "react";
import { cqAPI } from "../../lib/Auth";
import {
  Box,
  Flex,
  SimpleGrid,
  Text,
  Stack,
  Button,
  VStack,
  IconButton,
  useToast,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrash,
  faCreditCard,
  faCircle,
} from "@fortawesome/free-solid-svg-icons";
import {
  faCcVisa,
  faCcMastercard,
  faCcAmex,
  faCcDiscover,
} from "@fortawesome/free-brands-svg-icons";
import dialogManager from "../DialogManager/DialogManager";
import EditableText from "../EditableText/EditableText";

function Subscription() {
  const [editMode, setEditMode] = useState(false);
  const [stagedChange, setStagedChange] = useState(null);
  const [availableBundles, setAvailableBundles] = useState([]);
  const [activeBundle, setActiveBundle] = useState(null);
  const [displayedBundle, setDisplayedBundle] = useState(null);
  const [card, setCard] = useState({});
  const [stagedChanges, setStagedChanges] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [currentUsages, setCurrentUsages] = useState([]);
  const [existingCurrentUsages, setExistingCurrentUsages] = useState([]);
  
  const toast = useToast();

  const fetchBundles = async () => {
    const response = await cqAPI.get("/settings/subscriptions/bundles");
    setAvailableBundles(response.data.bundles || []);
    setActiveBundle(response.data.active_bundle || null);
    setDisplayedBundle(response.data.active_bundle || null);
  };
  
  const fetchPlans = async () => {
    const response = await cqAPI.get("/settings/subscriptions");
    setAvailableProducts(response.data.availableProducts);
    setCurrentUsages(response.data.currentUsages);
    setExistingCurrentUsages(response.data.currentUsages)
    setCard(response.data.paymentMethod || {});
  };

  const toggleEditMode = () => {
    setEditMode(!editMode);
    if (!editMode) {
      setStagedChange(null);
      setDisplayedBundle(activeBundle);
    }
  };

  const stageChange = (bundle_id) => {
    setStagedChange(bundle_id);
    setDisplayedBundle(bundle_id);
  };
  
  const saveChanges = async () => {
    const simplifiedChanges = simplifyStagedChanges()
    // Assuming an API call to save all changes at once
    const response = await cqAPI.put(
      `/settings/subscriptions/batch-update?bundle_id=${stagedChange || ""}`,
      simplifiedChanges
    );

    if (response.type === "error") {
      dialogManager.open({
        onDeny: () => {},
        acceptText: "OK",
        body: response.msg,
        header: "Error",
      });
      return;
    } else {
      fetchPlans().then(()=>fetchBundles());
      setStagedChanges([]);
      setEditMode(false)
      setActiveBundle(stagedChange);
      setStagedChange(null);
    }
  };

  useEffect(() => {
    fetchPlans().then(()=>fetchBundles());
  }, []);

  const requestCreditCardChange = async () => {
    const response = await cqAPI.requestCreditCard({});
    if (response) {
      setCard(response);
    } else {
      // Handle error or cancellation
    }
  };

  const cardBrandIcon = (brand) => {
    switch (brand) {
      case "Visa":
        return faCcVisa;
      case "MasterCard":
        return faCcMastercard;
      case "American Express":
        return faCcAmex;
      case "Discover":
        return faCcDiscover;
      default:
        return faCreditCard;
    }
  };
  
  useEffect(()=>{},[currentUsages])
  
  const stageBatchChange = (change) => {
    setStagedChanges((prev) => [...prev, change]);
    const product = availableProducts.find((p) => p.id === change.product_id);

    setCurrentUsages((prevCurrentUsages) => {
      const isAlreadySubscribedToAnotherPlan = prevCurrentUsages.find((u) =>
        product.plans.find((p) => p.id === u.plan)
      );
    
      let updatedUsages;
      switch (change.type) {
        case "subscribe":
          const newPlan = product.plans.find((p) => p.id === change.plan_id);
          if (isAlreadySubscribedToAnotherPlan) {
            updatedUsages = [
              ...prevCurrentUsages.filter(
                (x) => x._id !== isAlreadySubscribedToAnotherPlan._id
              ),
              {
                ...isAlreadySubscribedToAnotherPlan,
                sub_id: undefined,
                sub_item_id: undefined,
                plan: change.plan_id,
                usage_type: newPlan.usage_type,
                _id: `temp-${Math.random().toString()}`,
                isNew: true
              },
            ];
          } else {
            updatedUsages = [
              ...prevCurrentUsages,
              {
                plan: change.plan_id,
                usage_type: newPlan.usage_type,
                _id: `temp-${Math.random().toString()}`, // Temporary ID
                amount: 1,
                isNew: true,
              },
            ];
          }
          break;
        case "changeLicenses":
          updatedUsages = [
            ...prevCurrentUsages.filter(
              (x) => x._id !== isAlreadySubscribedToAnotherPlan._id
            ),
            {
              ...isAlreadySubscribedToAnotherPlan,
              amount: change.amount,
            },
          ];
          break;
        case "delete":
          updatedUsages = prevCurrentUsages.filter((u) => u._id !== change.usage_id);
          break;
        default:
          updatedUsages = prevCurrentUsages;
          break;
      }

      return updatedUsages;
    });
  };
  
  
  useEffect(()=>{
    if (!displayedBundle){return}
    const activeBundlePlan = availableBundles.find(b=>b._id === activeBundle);
    const selectedBundlePlan = availableBundles.find(b=>b._id === displayedBundle);
    
    setCurrentUsages(existingCurrentUsages); //this reset to the original stage
    //now we remove all of them
    
    if (activeBundle === displayedBundle){return}

    selectedBundlePlan.items.forEach(item=>{
      if (item[1].recurring.usage_type === 'licensed'){
        const product = availableProducts.find(p=> p.plans.find(i=>i.id === item[1].id))
        const usage = existingCurrentUsages.find(usage=>product.plans.find(p=>p.id === usage.plan));
        if (usage){
          stageBatchChange({
            type: "subscribe",
            product_id: item[0].id,
            plan_id: item[1].id
          })
        }else{
          stageBatchChange({
            type: "subscribe",
            product_id: item[0].id,
            plan_id: item[1].id,
          })
        }
      }else{
        stageBatchChange({
          type: "subscribe",
          product_id: item[0].id,
          plan_id: item[1].id,
        })
      }
    })
    
    existingCurrentUsages.forEach(usage=>{
      const product = availableProducts.find(p=> p.plans.find(i=>i.id === usage.plan));
      const toBeAdded = selectedBundlePlan.items.find(item=>item[1].id === product.id)
      if (!toBeAdded){
        stageBatchChange({
          type: "delete",
          product_id: availableProducts.find(p=> p.plans.find(i=>i.id === usage.plan)).id,
          plan_id: usage.plan,
          usage_id: usage._id
        })
      }
    })
    
  },[displayedBundle])
  
  function simplifyStagedChanges() {
    const changes = currentUsages.reduce((acc, usage)=>{
      //detect if this is a change and if so what it is
      const existing = existingCurrentUsages.find(p=> p.plan === usage.plan)
      if (!existing){
        acc.push({
          ...usage,
          type : 'subscribe'
        })
      }else{
        if (existing.amount !== usage.amount){
          acc.push({
            ...usage,
            type : 'changeLicenses'
          })
        }
      }
      return acc
    },[])
    //now chech if there are any deletes
    const deleted = existingCurrentUsages.reduce((acc, usage)=>{
      const existing = currentUsages.find(p=> p.plan === usage.plan)
      const product = availableProducts.find(p=> p.plans.find(i=>i.id === usage.plan))
      if (!existing){
        const hasProductDifferentPlan = product.plans.find(p=> currentUsages.find(i=>i.plan === p.id))
        if (!hasProductDifferentPlan){
          acc.push({
            ...usage,
            type : 'delete'
          })
        }
      }
      return acc
    },[])
    
    return deleted.concat(changes)
    
  }

  function CreditCardDisplay({ last4, brand }) {
    return (
      <Flex align="center">
        <FontAwesomeIcon icon={cardBrandIcon(brand)} />{" "}
        <Flex align="center" justify="center">
          <FontAwesomeIcon
            icon={faCircle}
            style={{ fontSize: "6px", paddingLeft: "10px" }}
          />
          <FontAwesomeIcon
            icon={faCircle}
            style={{ fontSize: "6px", paddingLeft: "1px" }}
          />
          <FontAwesomeIcon
            icon={faCircle}
            style={{ fontSize: "6px", paddingLeft: "1px" }}
          />
          <FontAwesomeIcon
            icon={faCircle}
            style={{ fontSize: "6px", paddingLeft: "1px" }}
          />
          <FontAwesomeIcon
            icon={faCircle}
            style={{ fontSize: "6px", paddingLeft: "10px" }}
          />
          <FontAwesomeIcon
            icon={faCircle}
            style={{ fontSize: "6px", paddingLeft: "1px" }}
          />
          <FontAwesomeIcon
            icon={faCircle}
            style={{ fontSize: "6px", paddingLeft: "1px" }}
          />
          <FontAwesomeIcon
            icon={faCircle}
            style={{ fontSize: "6px", paddingLeft: "1px" }}
          />
          <FontAwesomeIcon
            icon={faCircle}
            style={{ fontSize: "6px", paddingLeft: "10px" }}
          />
          <FontAwesomeIcon
            icon={faCircle}
            style={{ fontSize: "6px", paddingLeft: "1px" }}
          />
          <FontAwesomeIcon
            icon={faCircle}
            style={{ fontSize: "6px", paddingLeft: "1px" }}
          />
          <FontAwesomeIcon
            icon={faCircle}
            style={{
              fontSize: "6px",
              paddingLeft: "1px",
              paddingRight: "10px",
            }}
          />
        </Flex>{" "}
        <Text>{last4}</Text>
      </Flex>
    );
  }

  const formatTieredPricing = (tiers, tiers_mode, unit, usage_type) => {
    return tiers.map((tier, index) => {
      const pricePerUnit = `$${(tier.unit_amount_decimal / 100).toFixed(
        3
      )}/${unit} ${usage_type === "licensed" ? "per month" : ""}`;
      if (tier.up_to === null) {
        return (
          <Text key={index}>
            Anything more or equal than {tiers[index - 1]?.up_to || 0} {unit}(s): {pricePerUnit}
          </Text>
        );
      } else {
        return (
          <Text key={index}>
            Tier {index + 1} between {tiers[index - 1]?.up_to || 0} and {tier.up_to} {unit}(s): {pricePerUnit}
          </Text>
        );
      }
    });
  };
  
  const changeLicenses = async (product_id, plan_id, usage_id, amount) => {
    const response = await cqAPI.put("/settings/subscriptions", {
      product_id,
      plan_id,
      usage_id,
      amount,
    });

    if (response.type == "error") {
      dialogManager.open({
        onDeny: () => {},
        acceptText: "OK",
        body: response.msg,
        header: "Error",
      });
      return false;
    }

    await fetchPlans().then(()=>fetchBundles());
    return true;
  };
  
  
  const getUsageForLicense = (product_id)=>{
    const product = availableProducts.find((p) => p.id === product_id);
    const existing = existingCurrentUsages.find(usage=>product.plans.find(p=>p.id == usage.plan))
    return {
      amount : existing? existing.amount : 1,
      existing_id : existing?._id
    }
  }

  return (
    <Box p={4} overflowY="auto" maxHeight="100vh" width="100%">
      <Flex justifyContent="right" padding="0px 0px 20px 0px">
        <Button onClick={toggleEditMode}>
          {editMode ? "Cancel Changes" : "Change Plan"}
        </Button>
        {editMode && (
          <Button ml={4} colorScheme="teal" onClick={saveChanges}>
            Save Changes
          </Button>
        )}
        <Button
          marginLeft="20px"
          leftIcon={<CreditCardDisplay last4={card.last4} brand={card.brand} />}
          onClick={requestCreditCardChange}
        >
          {!card.last4 && <Text>"Credit Card"</Text>}
        </Button>
      </Flex>
      <SimpleGrid columns={{ base: 1, md: 1, lg: 1 }} spacing="20px">
        {availableBundles.map((bundle, index) => (
          <Box
            key={index}
            borderWidth="1px"
            borderRadius="lg"
            mb={4}
            width="100%"
          >
            <Box
              bg="blue.500"
              color="white"
              p={4}
              borderTopRadius="lg"
              position="relative"
            >
              <Text>{bundle.name}</Text>
              <Text>{bundle.description}</Text>
              {displayedBundle === bundle._id && (
                <Text color="green.500">
                  Selected {!bundle.active && "(grandfathered)"}
                </Text>
              )}
            </Box>
            <Box p={4}>
              <VStack align="left">
                {bundle.items.map(([product, plan], itemIndex) => (
                  <Flex
                    justify="space-between"
                    key={itemIndex}
                    mb={4}
                  >
                    <Box flex="1" textAlign="left" ml={4}>
                      <Text fontWeight="bold" paddingRight="10px">{product.name}:</Text>
                      {plan.billing_scheme === "tiered" ? (
                        <>
                          {formatTieredPricing(
                            plan.tiers,
                            plan.tiers_mode,
                            product.unit_label,
                            plan.usage_type
                          )}
                        </>
                      ) : (
                        <>
                          <Text>
                            Price: ${(
                              plan.unit_amount_decimal / 100
                            ).toFixed(3)}/
                            {product.unit_label}{" "}
                            {plan.usage_type === "licensed" ? "per month" : ""}
                          </Text>
                            {activeBundle === bundle._id ? (
                              <Flex >
                            <EditableText
                              initialValue={getUsageForLicense(product.id).amount}
                              onSave={(e) =>
                                changeLicenses(product.id, plan.id, getUsageForLicense(product.id).existing_id, parseInt(e))
                              }
                              placeholder="Licenses"
                              pattern={/[1-9]+\d*$/}
                              invalid="Only numbers greater than 1 are acceptable."
                            />
                              </Flex>
                            ) : (
                              displayedBundle === bundle._id && (
                                <Flex justify="flex-end" align="center">{getUsageForLicense(product.id).amount}</Flex>
                              )
                            )}
                        </>
                      )}
                    </Box>
                  </Flex>
                ))}
              </VStack>
              {editMode && displayedBundle !== bundle._id && (
                <Button
                  size="sm"
                  mt={4}
                  onClick={() => stageChange(bundle._id)}
                >
                  Subscribe
                </Button>
              )}
              {editMode && displayedBundle === bundle._id && (
                <Button
                  size="sm"
                  mt={4}
                  onClick={() => stageChange(null)}
                >
                  Unsubscribe
                </Button>
              )}
            </Box>
          </Box>
        ))}
      </SimpleGrid>
    </Box>
  );
}

export default Subscription;
